// File: src/pages/candidate/SavedJobsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SavedJobsPage() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    useEffect(() => {
        const fetchSavedJobs = async () => {
            const token = localStorage.getItem('skillmatch_token');
            if (!token) return navigate('/login');
            try {
                const res = await fetch(`${API_URL}/api/users/saved-jobs`, { headers: { 'x-auth-token': token } });
                if (res.ok) setJobs(await res.json());
            } catch (error) { console.error("Lỗi:", error); } finally { setLoading(false); }
        };
        fetchSavedJobs();
    }, [navigate, API_URL]);

    const handleUnsave = async (jobId, e) => {
        e.stopPropagation();
        const token = localStorage.getItem('skillmatch_token');
        try {
            await fetch(`${API_URL}/api/users/toggle-job/${jobId}`, { method: 'PUT', headers: { 'x-auth-token': token } });
            setJobs(jobs.filter(j => j._id !== jobId));
        } catch (err) { console.error(err); }
    };

    if (loading) return (
        <div className="min-h-screen pt-24 flex items-center justify-center bg-[#F8FAFC]">
            <span className="material-symbols-outlined animate-spin text-3xl text-blue-600">sync</span>
        </div>
    );

    return (
        <div className="bg-[#F8FAFC] min-h-screen pt-28 pb-12 px-6">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4 animate-fade-up">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Việc làm đã lưu</h1>
                        <p className="text-gray-500 mt-2 font-medium">Danh sách các công việc bạn đang quan tâm.</p>
                    </div>
                    <div className="bg-white px-5 py-2 rounded-full shadow-sm border border-gray-200 text-sm font-bold text-indigo-600 flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">bookmark</span>
                        {jobs.length} Công việc
                    </div>
                </div>
                
                {jobs.length === 0 ? (
                    <div className="bg-white rounded-3xl p-16 text-center shadow-lg border border-gray-100 animate-fade-up">
                        <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-5xl text-indigo-400">bookmark_border</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Chưa lưu công việc nào</h3>
                        <p className="text-gray-500 mt-2 mb-8">Lưu lại các việc làm hấp dẫn để xem lại và ứng tuyển sau.</p>
                        <button onClick={() => navigate('/jobs')} className="btn-signature px-8 py-3 text-sm shadow-indigo-500/20 bg-gradient-to-r from-indigo-600 to-purple-600">Tìm việc ngay</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {jobs.map((job, idx) => (
                            <div 
                                key={job._id} 
                                onClick={() => navigate(`/jobs/${job._id}`)} 
                                className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col h-full relative animate-fade-up"
                                style={{ animationDelay: `${idx * 0.05}s` }}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl flex items-center justify-center text-xl font-bold text-indigo-600 border border-indigo-100 shadow-inner">
                                        {job.companyName?.charAt(0)}
                                    </div>
                                    <button 
                                        onClick={(e) => handleUnsave(job._id, e)}
                                        className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 hover:border-red-200 transition-all shadow-sm z-10"
                                        title="Bỏ lưu"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">bookmark_remove</span>
                                    </button>
                                </div>

                                <h3 className="font-bold text-lg text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2 mb-1 min-h-[56px]">
                                    {job.title}
                                </h3>
                                <p className="text-sm text-gray-500 font-medium mb-4 truncate">{job.companyName}</p>

                                <div className="mt-auto space-y-3">
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-100">
                                            {job.salary}
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                                            {job.location}
                                        </span>
                                    </div>
                                    <div className="pt-3 border-t border-gray-50 flex justify-between items-center text-xs text-gray-400">
                                        <span>{new Date(job.createdAt).toLocaleDateString('vi-VN')}</span>
                                        <span className="group-hover:translate-x-1 transition-transform text-indigo-500 font-semibold flex items-center">
                                            Chi tiết <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}